import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, useTheme } from './context/ThemeContext'
import { ToastProvider } from './context/ToastContext'
import { TenantProvider, useTenant } from './context/TenantContext'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Logo from './components/ui/Logo'
import GlobalAnnouncements from './components/ui/GlobalAnnouncements'
import Button from './components/ui/Button'
import { useAuth } from './hooks/useAuth'
import { Capacitor } from '@capacitor/core'
import { Component, lazy, Suspense } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

const LandingPage = lazy(() => import('./pages/LandingPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const OrganizationDashboard = lazy(() => import('./pages/OrganizationDashboard'))
const TeamManagement = lazy(() => import('./components/team/TeamManagement'))
const RolesManagement = lazy(() => import('./components/roles/RolesManagement'))
const ProjectsView = lazy(() => import('./components/projects/ProjectsView'))
const SurveysManagement = lazy(() => import('./components/surveys/SurveysManagement'))
const ExternalListsManagement = lazy(() => import('./components/surveys/ExternalListsManagement'))
const AssignmentsManagement = lazy(() => import('./components/assignments/AssignmentsManagement'))
const InterviewsManagement = lazy(() => import('./components/interviews/InterviewsManagement'))
const SubmissionsManagement = lazy(() => import('./components/interviews/SubmissionsManagement'))
const LocationsManagement = lazy(() => import('./components/locations/LocationsManagement'))
const SettingsManagement = lazy(() => import('./components/settings/SettingsManagement'))
const AnnouncementsManagement = lazy(() => import('./components/announcements/AnnouncementsManagement'))
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'))
const SupportTickets = lazy(() => import('./components/support/SupportTickets'))
const FieldAchievement = lazy(() => import('./components/analytics/FieldAchievement'))
const NotificationsManagement = lazy(() => import('./components/notifications/NotificationsManagement'))
const SuperAdminDashboard = lazy(() => import('./components/admin/SuperAdminDashboard'))
const SuperAdminTiersPage = lazy(() => import('./components/admin/SuperAdminTiersPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const BillingPage = lazy(() => import('./pages/BillingPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const FAQPage = lazy(() => import('./pages/FAQPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'))
const DashboardBuilder = lazy(() => import('./components/dashboard/builder/DashboardBuilder'))
const LaunchpadPage = lazy(() => import('./components/dashboard/LaunchpadPage'))
const DashboardsList = lazy(() => import('./components/dashboard/DashboardsList'))
const PublicSurveyPage = lazy(() => import('./pages/PublicSurveyPage'))

export const preloadDashboardChunks = () => {
  try {
    import('./components/dashboard/LaunchpadPage')
    import('./components/projects/ProjectsView')
    import('./components/surveys/SurveysManagement')
    import('./components/team/TeamManagement')
    import('./components/roles/RolesManagement')
    import('./components/interviews/SubmissionsManagement')
    import('./components/locations/LocationsManagement')
    import('./components/settings/SettingsManagement')
    import('./components/assignments/AssignmentsManagement')
    import('./components/surveys/ExternalListsManagement')
    import('./components/interviews/InterviewsManagement')
    import('./components/analytics/FieldAchievement')
    import('./components/announcements/AnnouncementsManagement')
    import('./components/support/SupportTickets')
    import('./components/notifications/NotificationsManagement')
    import('./components/dashboard/builder/DashboardBuilder')
    import('./components/dashboard/DashboardsList')
  } catch {
    // Ignore preload failures
  }
}
const PublicDashboardPage = lazy(() => import('./pages/PublicDashboardPage'))
const SurveyBuilderPage = lazy(() => import('./pages/SurveyBuilderPage'))
const SurveyEntryPage = lazy(() => import('./pages/SurveyEntryPage'))

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught Error in Component Tree:', error, errorInfo)
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-900 text-white p-6 text-center">
          <div className="w-16 h-16 bg-red-500/20 text-red-400 rounded-2xl flex items-center justify-center mb-4">
            <span className="text-2xl font-black">!</span>
          </div>
          <h2 className="text-xl font-black mb-2">Something went wrong</h2>
          <p className="text-xs text-neutral-400 max-w-md mb-6 break-words font-mono bg-neutral-800 p-3 rounded-xl">
            {this.state.error?.message || 'Unknown runtime error'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-primary-500 text-white text-xs font-black rounded-xl shadow-lg hover:bg-primary-600 cursor-pointer"
          >
            Reload Application
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth()
  const { t } = useTheme()

  if (isLoading) {
    return <PageSkeletonLoader text={t('common.loading_identity', 'Verifying identity...')} />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

const AnonymousOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user, isLoading } = useAuth()
  const { isDomainBased } = useTenant()
  const { t } = useTheme()

  if (isLoading) {
    return <PageSkeletonLoader text={t('common.loading_identity', 'Verifying identity...')} />
  }

  if (isAuthenticated && user) {
    if (user.is_superuser) {
      return <Navigate to="/system-admin" replace />
    }
    
    if (isDomainBased || Capacitor.isNativePlatform()) {
      return <Navigate to="/" replace />
    } else {
      const orgSlug = user.organization_slug || 'system'
      return <Navigate to={`/${orgSlug}`} replace />
    }
  }

  return <>{children}</>
}

const SuperAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const { t } = useTheme()

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 gap-5">
        <div className="relative w-20 h-20 flex items-center justify-center">
          <div className="absolute inset-0 bg-primary-500/10 rounded-3xl animate-ping opacity-75" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 rounded-3xl blur-xl opacity-20 animate-pulse" />
          <div className="relative w-16 h-16 bg-white border border-neutral-100/60 rounded-2xl flex items-center justify-center shadow-lg p-2.5 animate-pulse">
            <Logo className="w-full h-full" showBackground={false} />
          </div>
        </div>
        <p className="text-neutral-400 font-black animate-pulse uppercase tracking-widest text-[10px] mt-2">
          {t('common.loading_permissions', 'Verifying permissions...')}
        </p>
      </div>
    )
  }

  if (!isAuthenticated || !user?.is_superuser) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

const Dashboard = () => {
  const { theme, toggleTheme, language, toggleLanguage, t } = useTheme();

  return (
    <div className="p-8 max-w-7xl mx-auto font-sans">
      <header className="flex justify-between items-center mb-12">
        <div className="flex items-center space-x-8 rtl:space-x-reverse">
          <Logo className="w-24 h-24" />
          <div>
            <h1 className="text-5xl font-black text-primary-500 tracking-tight">
              {t('common.estipana', 'Estipana')}
            </h1>
            <p className="mt-2 text-md text-neutral-500 font-medium">{t('landing.hero_subtitle', 'NGO Data Collection & Analysis Platform')}</p>
          </div>
        </div>

        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <button
            onClick={toggleLanguage}
            className="px-3 py-1.5 text-sm font-bold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            {language === 'primary' || language === 'en' ? 'العربية' : 'English'}
          </button>
          <button
            onClick={toggleTheme}
            className="px-4 py-2 bg-surface border border-neutral-200 rounded-lg shadow-sm text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            {t('common.switch_to', 'Switch to')} {theme === 'public' ? t('common.admin', 'Admin') : t('common.public', 'Public')}
          </button>
        </div>
      </header>

      <main className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-surface rounded-2xl shadow-sm border border-neutral-100 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider">{t('surveys.total_surveys', 'Total Surveys')}</h3>
            <span className="p-2 bg-primary-50 rounded-lg text-primary-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 0 -2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            </span>
          </div>
          <p className="text-3xl font-bold text-neutral-900">1,248</p>
          <div className="mt-4 flex items-center text-sm text-success">
            <span className="flex items-center gap-1">↑ 12% {t('common.last_month', 'since last month')}</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl shadow-lg p-6 text-white md:col-span-2 relative overflow-hidden group">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-surface opacity-10 rounded-full blur-xl group-hover:opacity-20 transition-opacity"></div>
          <h3 className="text-xl font-bold mb-2">{t('surveys.import_excel', 'Import Excel Schema')}</h3>
          <p className="text-primary-100 mb-6 max-w-md">{t('surveys.drag_drop', 'Drag and drop Excel file here to create survey structures automatically.')}</p>

          <Button variant="outline" className="bg-surface text-primary-600 border-none hover:bg-primary-50">
            {t('common.upload', 'Upload File')}
          </Button>
        </div>

        <div className="md:col-span-3 bg-surface rounded-2xl shadow-sm border border-neutral-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 bg-neutral-50/50 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-neutral-800">{t('interviews.recent_submissions', 'Recent Submissions')}</h3>
            <span className="text-xs font-semibold text-primary-500 uppercase cursor-pointer hover:underline">{t('common.view_all', 'View All')}</span>
          </div>
          <div className="divide-y divide-neutral-100">
            {[1, 2, 3].map((i) => (
              <div key={i} className="px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 font-bold">U{i}</div>
                  <div>
                    <p className="text-sm font-medium text-neutral-900">{t('interviews.education_assessment', 'Education Needs Assessment')}</p>
                    <p className="text-xs text-neutral-500">{t('interviews.submitted_in', 'Submitted in Damascus')} • {t('interviews.time_ago', '2 hours ago')}</p>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-success/10 text-success">{t('interviews.status.approved', 'Verified')}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}


const PageSkeletonLoader = ({ text }: { text?: string }) => (
  <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 font-sans p-4 sm:p-8 animate-pulse transition-colors">
    {/* Header Navbar Skeleton */}
    <header className="flex justify-between items-center mb-8 pb-4 border-b border-neutral-200/60 dark:border-neutral-800 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-neutral-200 dark:bg-neutral-800" />
        <div className="space-y-1.5">
          <div className="w-28 h-4 rounded-lg bg-neutral-200 dark:bg-neutral-800" />
          <div className="w-40 h-3 rounded-lg bg-neutral-200/60 dark:bg-neutral-800/60" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-neutral-200 dark:bg-neutral-800" />
        <div className="w-9 h-9 rounded-xl bg-neutral-200 dark:bg-neutral-800" />
      </div>
    </header>

    <div className="max-w-7xl mx-auto space-y-6">
      {/* Hero Section Skeleton */}
      <div className="p-6 sm:p-10 rounded-3xl bg-neutral-200/60 dark:bg-neutral-900 border border-neutral-200/60 dark:border-neutral-800 space-y-4">
        <div className="w-24 h-6 rounded-full bg-neutral-300 dark:bg-neutral-800" />
        <div className="w-2/3 h-8 sm:h-10 rounded-xl bg-neutral-300 dark:bg-neutral-800" />
        <div className="w-1/2 h-4 rounded-lg bg-neutral-300/70 dark:bg-neutral-800/70" />
      </div>

      {/* Content Cards Skeleton Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-6 rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200/60 dark:border-neutral-800 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <div className="w-20 h-4 rounded-lg bg-neutral-200 dark:bg-neutral-800" />
              <div className="w-8 h-8 rounded-xl bg-neutral-100 dark:bg-neutral-800" />
            </div>
            <div className="w-32 h-8 rounded-xl bg-neutral-200 dark:bg-neutral-800" />
            <div className="w-full h-3 rounded-lg bg-neutral-100 dark:bg-neutral-800/60" />
          </div>
        ))}
      </div>

      {/* Bottom Status Banner */}
      <div className="flex items-center justify-center gap-3 pt-6">
        <div className="w-5 h-5 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        <span className="text-xs font-bold text-neutral-400 uppercase tracking-widest">
          {text || 'Loading Estipana...'}
        </span>
      </div>
    </div>
  </div>
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000,    // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})


function AppRoutes() {
  const { isDomainBased, isLoadingOrg } = useTenant();

  if (isLoadingOrg) {
    return <PageSkeletonLoader text="Loading environment..." />
  }

  return (
    <Suspense fallback={<PageSkeletonLoader text="Loading page..." />}>
      <Routes>
        {isDomainBased ? (
          <>
            <Route path="/login" element={<AnonymousOnlyRoute><LoginPage /></AnonymousOnlyRoute>} />
            <Route path="/register" element={<AnonymousOnlyRoute><RegisterPage /></AnonymousOnlyRoute>} />
            <Route path="/pricing" element={<AnonymousOnlyRoute><PricingPage /></AnonymousOnlyRoute>} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <OrganizationDashboard />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="launchpad" replace />} />
              <Route path="launchpad" element={<LaunchpadPage />} />
              <Route path="home" element={<LaunchpadPage />} />
              <Route path="projects" element={<ProjectsView />} />
              <Route path="team" element={<TeamManagement />} />
              <Route path="roles" element={<RolesManagement />} />
              <Route path="surveys" element={<SurveysManagement />} />
              <Route path="surveys/builder" element={<SurveyBuilderPage />} />
              <Route path="surveys/builder/:surveyId" element={<SurveyBuilderPage />} />
              <Route path="surveys/entry/:surveyId" element={<SurveyEntryPage />} />
              <Route path="external-lists" element={<ExternalListsManagement />} />
              <Route path="dashboard-builder" element={<DashboardsList />} />
              <Route path="dashboard-builder/builder/dashboard/:dashboardId" element={<DashboardBuilder />} />
              <Route path="dashboard-builder/builder/:surveyId" element={<DashboardBuilder />} />
              <Route path="dashboard-builder/builder" element={<DashboardBuilder />} />
              <Route path="dashboards" element={<Navigate to="dashboard-builder" replace />} />
              <Route path="assignments" element={<AssignmentsManagement />} />
              <Route path="submissions" element={<SubmissionsManagement />} />
              <Route path="interviews" element={<SubmissionsManagement />} />
              <Route path="field-achievement" element={<FieldAchievement />} />
              <Route path="my-tasks" element={<InterviewsManagement />} />
              <Route path="locations" element={<LocationsManagement />} />
              <Route path="announcements" element={<AnnouncementsManagement />} />
              <Route path="settings" element={<SettingsManagement />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="support" element={<SupportTickets />} />
              <Route path="notifications" element={<NotificationsManagement />} />
              <Route path="profile" element={<ProfileSettings />} />
            </Route>
          </>
        ) : (
          <>
            <Route path="/" element={<AnonymousOnlyRoute><LandingPage /></AnonymousOnlyRoute>} />
            <Route path="/register" element={<AnonymousOnlyRoute><RegisterPage /></AnonymousOnlyRoute>} />
            <Route path="/login" element={<AnonymousOnlyRoute><LoginPage /></AnonymousOnlyRoute>} />
            <Route path="/pricing" element={<AnonymousOnlyRoute><PricingPage /></AnonymousOnlyRoute>} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route element={
              <ProtectedRoute>
                <OrganizationDashboard />
              </ProtectedRoute>
            }>
              <Route path="/launchpad" element={<LaunchpadPage />} />
              <Route path="/home" element={<LaunchpadPage />} />
              <Route path="/projects" element={<ProjectsView />} />
              <Route path="/team" element={<TeamManagement />} />
              <Route path="/roles" element={<RolesManagement />} />
              <Route path="/surveys" element={<SurveysManagement />} />
              <Route path="/surveys/builder" element={<SurveyBuilderPage />} />
              <Route path="/surveys/builder/:surveyId" element={<SurveyBuilderPage />} />
              <Route path="/surveys/entry/:surveyId" element={<SurveyEntryPage />} />
              <Route path="/external-lists" element={<ExternalListsManagement />} />
              <Route path="/dashboard-builder" element={<DashboardsList />} />
              <Route path="/dashboard-builder/builder/dashboard/:dashboardId" element={<DashboardBuilder />} />
              <Route path="/dashboard-builder/builder/:surveyId" element={<DashboardBuilder />} />
              <Route path="/dashboard-builder/builder" element={<DashboardBuilder />} />
              <Route path="/dashboards" element={<Navigate to="/dashboard-builder" replace />} />
              <Route path="/assignments" element={<AssignmentsManagement />} />
              <Route path="/submissions" element={<SubmissionsManagement />} />
              <Route path="/interviews" element={<SubmissionsManagement />} />
              <Route path="/field-achievement" element={<FieldAchievement />} />
              <Route path="/my-tasks" element={<InterviewsManagement />} />
              <Route path="/locations" element={<LocationsManagement />} />
              <Route path="/announcements" element={<AnnouncementsManagement />} />
              <Route path="/settings" element={<SettingsManagement />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/support" element={<SupportTickets />} />
              <Route path="/notifications" element={<NotificationsManagement />} />
              <Route path="/profile" element={<ProfileSettings />} />
            </Route>
            <Route path="/:orgSlug" element={
              <ProtectedRoute>
                <OrganizationDashboard />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="launchpad" replace />} />
              <Route path="launchpad" element={<LaunchpadPage />} />
              <Route path="home" element={<LaunchpadPage />} />
              <Route path="projects" element={<ProjectsView />} />
              <Route path="team" element={<TeamManagement />} />
              <Route path="roles" element={<RolesManagement />} />
              <Route path="surveys" element={<SurveysManagement />} />
              <Route path="surveys/builder" element={<SurveyBuilderPage />} />
              <Route path="surveys/builder/:surveyId" element={<SurveyBuilderPage />} />
              <Route path="surveys/entry/:surveyId" element={<SurveyEntryPage />} />
              <Route path="external-lists" element={<ExternalListsManagement />} />
              <Route path="dashboard-builder" element={<DashboardsList />} />
              <Route path="dashboard-builder/builder/dashboard/:dashboardId" element={<DashboardBuilder />} />
              <Route path="dashboard-builder/builder/:surveyId" element={<DashboardBuilder />} />
              <Route path="dashboard-builder/builder" element={<DashboardBuilder />} />
              <Route path="dashboards" element={<Navigate to="dashboard-builder" replace />} />
              <Route path="assignments" element={<AssignmentsManagement />} />
              <Route path="submissions" element={<SubmissionsManagement />} />
              <Route path="interviews" element={<SubmissionsManagement />} />
              <Route path="field-achievement" element={<FieldAchievement />} />
              <Route path="my-tasks" element={<InterviewsManagement />} />
              <Route path="locations" element={<LocationsManagement />} />
              <Route path="announcements" element={<AnnouncementsManagement />} />
              <Route path="settings" element={<SettingsManagement />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="support" element={<SupportTickets />} />
              <Route path="notifications" element={<NotificationsManagement />} />
              <Route path="profile" element={<ProfileSettings />} />
            </Route>
          </>
        )}
        <Route path="/old-dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/system-admin" element={
          <SuperAdminRoute>
            <OrganizationDashboard />
          </SuperAdminRoute>
        }>
          <Route index element={<SuperAdminDashboard />} />
          <Route path="tiers" element={<SuperAdminTiersPage />} />
        </Route>
        <Route path="/admin/roles" element={
          <ProtectedRoute>
            <div className="p-8 max-w-7xl mx-auto font-sans">
              <RolesManagement />
            </div>
          </ProtectedRoute>
        } />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/s/:orgSlug/:surveySlugAndToken" element={<PublicSurveyPage />} />
        <Route path="/public/dashboards/:publicId" element={<PublicDashboardPage />} />
        <Route path="/d/:publicId" element={<PublicDashboardPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <TenantProvider>
          <ThemeProvider>
            <ToastProvider>
              <GlobalAnnouncements />
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </ToastProvider>
          </ThemeProvider>
        </TenantProvider>
      </Router>
    </QueryClientProvider>
  )
}

export default App
